import { useLayout } from 'context'
import { NeedsClusterRemovedDetailsDto } from 'dtos/needsClusterRemovedDetailsDto'
import { NeedsClusterRemovedPulledClusterPhotoDto } from 'dtos/needsClusterRemovedPulledClusterPhotoDto'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import * as controllers from 'controllers'
import { ArrowBack } from '@mui/icons-material'
import { Grid, Card, CardContent, Typography, Button } from '@mui/material'
import VehicleClusterRemovedHeroCard from './VehicleClusterRemovedHeroCard'
import Step3PulledClusterReview from './Step3PulledClusterReview'
import { enqueueSnackbar } from 'notistack'
import Step2PulledClusterPhoto from './Step2PulledClusterPhoto'
import Step1ScanClusterBarCode from './Step1ScanClusterBarCode'

export interface VehicleNeedsClusterRemovedPhotoProps {
  pulledClusterPhoto: NeedsClusterRemovedPulledClusterPhotoDto
  setPulledClusterPhoto: React.Dispatch<React.SetStateAction<NeedsClusterRemovedPulledClusterPhotoDto>>
}

export default function NeedsClusterRemoved() {
  const { vehicleGuid } = useParams()
  const layout = useLayout()
  const navigate = useNavigate()

  const [initialValues, setInitialValues] = useState<NeedsClusterRemovedDetailsDto>(new NeedsClusterRemovedDetailsDto())

  const [activeStep, setActiveStep] = useState<number>(0)
  const [pulledClusterPhoto, setPulledClusterPhoto] = useState<NeedsClusterRemovedPulledClusterPhotoDto>(
    new NeedsClusterRemovedPulledClusterPhotoDto()
  )

  useEffect(() => {
    if (vehicleGuid) {
      layout.setIsLayoutLoading(true)
      controllers
        .getNeedsClusterRemovedDetails(vehicleGuid)
        .then(response => {
          setInitialValues(response.value)
        })
        .finally(() => {
          layout.setIsLayoutLoading(false)
        })

      controllers.getNeedsClusterRemovedPulledClusterPhotoInformation(vehicleGuid).then(response => {
        setPulledClusterPhoto(response.value)
      })
    }
  }, [vehicleGuid])

  const onBack = () => {
    setActiveStep(prev => Math.max(0, prev - 1))
  }

  const onNext = () => {
    setActiveStep(prev => Math.min(3, prev + 1))
  }

  return (
    <Grid container spacing={1} alignItems='center'>
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Grid container justifyContent='space-between'>
              <Grid item>
                <Typography fontWeight='bold' variant='h5' sx={{ pr: 1 }}>
                  Needs Cluster Removed
                </Typography>
              </Grid>

              <Grid item xs={12} sm='auto' container spacing={2} alignItems='center' justifyContent='flex-end'>
                <Grid item>
                  <Button
                    onClick={() => {
                      navigate('/to-do-list')
                    }}
                    color='primary'
                    startIcon={<ArrowBack />}
                    variant='text'
                  >
                    BACK
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <VehicleClusterRemovedHeroCard vehicle={initialValues} />
      </Grid>

      {activeStep === 0 && (
        <Grid item xs={12}>
          <Step1ScanClusterBarCode initialValues={initialValues} onNext={onNext} vehicle={initialValues} />
        </Grid>
      )}

      {activeStep === 1 && (
        <Grid item xs={12}>
          <Step2PulledClusterPhoto
            onBack={onBack}
            onNext={onNext}
            needsClusterRemovedDetails={initialValues}
            vehicleGuid={vehicleGuid}
            pulledClusterPhoto={pulledClusterPhoto}
            setPulledClusterPhoto={setPulledClusterPhoto}
          />
        </Grid>
      )}

      {activeStep === 2 && (
        <Grid item xs={12}>
          <Step3PulledClusterReview
            onBack={onBack}
            onNext={() => {
              layout.setIsLayoutLoading(true)

              controllers
                .updateNeedsClusterRemovedDetails(vehicleGuid!, initialValues)
                .then(response => {
                  enqueueSnackbar('Cluster Removed Process Completed Successfully!', { variant: 'success' })
                  // setInitialValues(response.value)
                  navigate('/to-do-list', { replace: true })
                })
                .finally(() => {
                  layout.setIsLayoutLoading(false)
                })
            }}
            vehicleNeedsClusterRemovedDetails={initialValues}
            vehicleGuid={vehicleGuid}
            pulledClusterPhoto={pulledClusterPhoto}
            setPulledClusterPhoto={setPulledClusterPhoto}
          />
        </Grid>
      )}
    </Grid>
  )
}
